import {useCallback, useMemo} from 'react';
import type {Matcher} from 'react-day-picker';
import {useSelector} from 'react-redux';

import {useGetAutoDeliveryDetailsExtendedDcx} from '@/api/consumerApi';
import {Calendar} from '@/components/Calendar';
import {formatMonthYearLabel, getWeekdayLabelsByDayIndex} from '@/components/Calendar/calendarDateUtils';
import appConfig from '@/config/appConfig.typed';
import {
    type DayOfWeekNumber,
    DEFAULT_WEEK_STARTS_ON,
    DEFAULT_WEEKEND_DAYS,
    mapDayOfWeekListToNumbers,
    mapDayOfWeekToNumber,
} from '@/config/types/calendarSettingsModel';
import appContent from '@/services/content/appContent.typed';
import {selectLanguageCode} from '@/state/selectors/global';
import {normalizeApiDate} from '@/utils/normalizeApiDate';

import RescheduleCalendarConfirmButton from './RescheduleCalendarConfirmButton';

type RescheduleCalendarProps = {
    selectedDate: Date | null;
    onDateSelect: (date: Date | null) => void;
    onConfirm: () => void;
    titleId: string;
    hideConfirmButton?: boolean;
};

const RescheduleCalendar = ({
    selectedDate,
    onDateSelect,
    onConfirm,
    titleId,
    hideConfirmButton = false,
}: RescheduleCalendarProps) => {
    const {weekStartsOn, weekendDays} = appConfig.getCalendarSettings() || {};
    const {excludeWeekends = false} = appConfig.getAutoDeliverySettings() || {};
    const selectDateContent = appContent.getAutoDeliveryContent()?.rescheduleDeliveryContent?.selectDateContent;
    const {data} = useGetAutoDeliveryDetailsExtendedDcx();
    const languageCode = useSelector(selectLanguageCode);
    const {nextOrderMinStartDate, nextOrderMaxStartDate} = data?.order || {};

    const {title, ariaLabelCalendar, prevMonthButton, nextMonthButton} = selectDateContent || {};
    const weekStartsOnNumber = mapDayOfWeekToNumber(weekStartsOn ?? DEFAULT_WEEK_STARTS_ON);
    const weekendDaysNumbers = mapDayOfWeekListToNumbers(weekendDays ?? DEFAULT_WEEKEND_DAYS);
    const startOfToday = useMemo(() => {
        const today = new Date();

        return new Date(today.getFullYear(), today.getMonth(), today.getDate());
    }, []);
    const minStartDate = useMemo(
        () => normalizeApiDate(nextOrderMinStartDate) ?? startOfToday,
        [nextOrderMinStartDate, startOfToday]
    );
    const maxStartDate = useMemo(() => normalizeApiDate(nextOrderMaxStartDate), [nextOrderMaxStartDate]);

    const weekdayLabels = useMemo(() => getWeekdayLabelsByDayIndex(languageCode), [languageCode]);
    const formatHeaderLabel = useCallback((date: Date) => formatMonthYearLabel(date, languageCode), [languageCode]);

    const disabledMatcher: Matcher = useCallback(
        (date: Date) => {
            const dayOfWeek = date.getDay() as DayOfWeekNumber;
            const isWeekend = excludeWeekends && weekendDaysNumbers.includes(dayOfWeek);
            const isBeforeRange = date < minStartDate;
            const isAfterRange = !!maxStartDate && date > maxStartDate;

            return isBeforeRange || isAfterRange || isWeekend;
        },
        [excludeWeekends, maxStartDate, minStartDate, weekendDaysNumbers]
    );

    const handleDateChange = useCallback(
        (date: Date | undefined) => {
            onDateSelect?.(date ?? null);
        },
        [onDateSelect]
    );

    const startMonth = useMemo(() => new Date(minStartDate.getFullYear(), minStartDate.getMonth(), 1), [minStartDate]);
    const endMonth = useMemo(
        () => (maxStartDate ? new Date(maxStartDate.getFullYear(), maxStartDate.getMonth(), 1) : undefined),
        [maxStartDate]
    );

    return (
        <div className='flex flex-col gap-8'>
            {title && (
                <h2 id={titleId} className='mb-0 text-28 ml:text-32'>
                    {title}
                </h2>
            )}
            <Calendar
                value={selectedDate ?? undefined}
                onChange={handleDateChange}
                weekdayLabels={weekdayLabels}
                weekStartsOn={weekStartsOnNumber}
                formatHeaderLabel={formatHeaderLabel}
                disabled={disabledMatcher}
                startMonth={startMonth}
                endMonth={endMonth}
                ariaLabel={ariaLabelCalendar}
                ariaLabelPrevMonth={prevMonthButton?.alt}
                ariaLabelNextMonth={nextMonthButton?.alt}
                className='m-auto max-w-[420px] md:w-full md:max-w-none'
            />
            {!hideConfirmButton && (
                <RescheduleCalendarConfirmButton selectedDate={selectedDate} onConfirm={onConfirm} />
            )}
        </div>
    );
};

export default RescheduleCalendar;
